import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from, map, switchMap } from 'rxjs';

import type {
	IOasisApplicationBase,
	OasisApiError,
	OasisApiResponse,
	OasisApplicationHeader,
	OasisApplicationListPage,
	OasisApplicationStatus,
	OasisApplicationView,
	OasisDocType,
	OasisUploadRef,
	PresignUploadResponse,
	PresignedGetResponse,
	PresignedPutResponse,
	UploadRequestInput,
} from 'shared-resources';

import { environment } from '../../../environments/environment';

export interface SaveApplicationResult {
	header: OasisApplicationHeader;
	created: boolean;
	statusChanged: boolean;
}

/**
 * Thin typed wrapper around the OASIS-external API. The auth interceptor
 * automatically attaches the Cognito access token to anything we call here.
 *
 * Each method consumes the standard response envelope
 * `{ status, success, payload, error }` and branches on `success` (never on the
 * HTTP status). A `success: false` envelope is surfaced as a thrown
 * `OasisApiError` on the observable's error channel; a not-found read is
 * `success: true` with a null payload.
 */
@Injectable({ providedIn: 'root' })
export class OasisApiService {
	private readonly http = inject(HttpClient);
	private readonly base = environment.oasisApiBase.replace(/\/$/, '');

	/** GET /oasis-application — applicant's own submission headers. */
	listMyApplications(cursor?: string): Observable<OasisApplicationListPage> {
		const url = cursor ? `${this.base}/oasis-application?cursor=${encodeURIComponent(cursor)}` : `${this.base}/oasis-application`;
		return this.http.get<OasisApiResponse<OasisApplicationListPage>>(url).pipe(map((r) => this.unwrap(r)));
	}

	/**
	 * GET /oasis-application/{id} — full view (header + body + events + flags).
	 * A not-found read returns `success: true` with `payload: null`, surfaced
	 * here as `null` so callers render a neutral empty state.
	 */
	getApplication(id: string): Observable<OasisApplicationView | null> {
		return this.http
			.get<OasisApiResponse<OasisApplicationView | null>>(`${this.base}/oasis-application/${encodeURIComponent(id)}`)
			.pipe(map((r) => this.unwrap(r) ?? null));
	}

	/**
	 * POST /oasis-application — save the body for the cycle.
	 *
	 * With `{ draft: true }` this hits `?intent=draft`, which persists the body as
	 * an `in_progress` draft and runs NONE of the submit-time review logic. Use it
	 * for per-screen "Save & Continue" autosaves. Without it, the server treats
	 * the call as a real submit/resubmit (deciding which by cycle window).
	 */
	saveApplication(body: IOasisApplicationBase, options: { draft?: boolean } = {}): Observable<SaveApplicationResult> {
		const url = options.draft ? `${this.base}/oasis-application?intent=draft` : `${this.base}/oasis-application`;
		return this.http.post<OasisApiResponse<SaveApplicationResult>>(url, body).pipe(map((r) => this.unwrap(r)));
	}

	/**
	 * POST /oasis-application/{id}/uploads — presign one or more files.
	 *
	 * The contract is a batch: `input.documents` is an array and the payload
	 * returns one presign per requested file, in request order.
	 */
	requestUpload(applicationId: string, input: UploadRequestInput): Observable<PresignUploadResponse> {
		return this.http
			.post<OasisApiResponse<PresignUploadResponse>>(`${this.base}/oasis-application/${encodeURIComponent(applicationId)}/uploads`, input)
			.pipe(map((r) => this.unwrap(r)));
	}

	/** Presign a single file and return just its target. */
	requestSingleUpload(applicationId: string, item: UploadRequestInput['documents'][number]): Observable<PresignedPutResponse> {
		return this.requestUpload(applicationId, { documents: [item] }).pipe(
			map((res) => {
				const target = res.documents?.[0];
				if (!target) {
					// A success envelope with no presign is a server contract break, not
					// something the applicant can act on.
					throw { code: 'PRESIGN_MISSING', message: 'The server did not return an upload target.' } satisfies OasisApiError;
				}
				return target;
			}),
		);
	}

	/** GET /oasis-application/{id}/uploads/{key+} — request a presigned GET URL. */
	getDownloadUrl(applicationId: string, s3Key: string): Observable<PresignedGetResponse> {
		// `key+` is a greedy path parameter — slashes inside the key must NOT be
		// percent-encoded, but everything else should be.
		const safeKey = s3Key.split('/').map(encodeURIComponent).join('/');
		return this.http
			.get<OasisApiResponse<PresignedGetResponse>>(`${this.base}/oasis-application/${encodeURIComponent(applicationId)}/uploads/${safeKey}`)
			.pipe(map((r) => this.unwrap(r)));
	}

	/**
	 * High-level helper: request a presigned URL, PUT the file directly to
	 * S3, and return the `OasisUploadRef` to embed in the application body.
	 *
	 * Uses `fetch` (not `HttpClient`) for the S3 PUT so the auth interceptor
	 * doesn't try to attach our Cognito token to AWS's signed URL.
	 */
	uploadFile(applicationId: string, file: File, docType: OasisDocType): Observable<OasisUploadRef> {
		const contentType = file.type || 'application/octet-stream';
		return this.requestSingleUpload(applicationId, {
			docType,
			filename: file.name,
			contentType,
			sizeBytes: file.size,
		}).pipe(
			switchMap((presigned) =>
				from(
					fetch(presigned.uploadUrl, {
						method: 'PUT',
						headers: { 'Content-Type': contentType },
						body: file,
					}).then((res) => {
						if (!res.ok) {
							throw new Error(`S3 upload failed: ${res.status} ${res.statusText}`);
						}
						const ref: OasisUploadRef = {
							s3Key: presigned.s3Key,
							docType,
							filename: file.name,
							contentType,
							sizeBytes: file.size,
							uploadedAt: new Date().toISOString(),
						};
						return ref;
					}),
				),
			),
		);
	}

	/** POST /oasis-application/{id}/withdraw — applicant withdraws (allowed any time until terminal). */
	withdrawApplication(id: string): Observable<{ header: OasisApplicationHeader }> {
		return this.http
			.post<OasisApiResponse<{ header: OasisApplicationHeader }>>(`${this.base}/oasis-application/${encodeURIComponent(id)}/withdraw`, {})
			.pipe(map((r) => this.unwrap(r)));
	}

	/**
	 * POST /oasis-application/{id}/program-change — start the HP → SLIP Law Clerk transfer.
	 *
	 * Re-opens the application for editing and DISCARDS every component review in
	 * progress. Returns which components were affected so the applicant can be told
	 * what stopped. Availability is decided server-side and surfaced as
	 * `flags.canRequestProgramChange`.
	 */
	requestProgramChange(id: string): Observable<{ header: OasisApplicationHeader; discardedComponents: string[] }> {
		return this.http
			.post<OasisApiResponse<{ header: OasisApplicationHeader; discardedComponents: string[] }>>(
				`${this.base}/oasis-application/${encodeURIComponent(id)}/program-change`,
				{},
			)
			.pipe(map((r) => this.unwrap(r)));
	}

	/**
	 * POST /oasis-application/{id}/reopen — undo a withdrawal.
	 *
	 * Restores the status the application held BEFORE the withdrawal rather than dropping
	 * it to a draft, and returns that status so the caller can say which one it was.
	 * Availability is decided server-side and surfaced as `flags.canReopen` — it depends
	 * on the write window and on the event history, so it cannot be derived from the
	 * status alone.
	 */
	reopenApplication(id: string): Observable<{ header: OasisApplicationHeader; restoredStatus: OasisApplicationStatus }> {
		return this.http
			.post<OasisApiResponse<{ header: OasisApplicationHeader; restoredStatus: OasisApplicationStatus }>>(
				`${this.base}/oasis-application/${encodeURIComponent(id)}/reopen`,
				{},
			)
			.pipe(map((r) => this.unwrap(r)));
	}

	/**
	 * POST /oasis-application/{id}/components/{code}/respond — the applicant's
	 * own component-lane actions: accept/decline an interview request or offer.
	 */
	respondToComponent(
		id: string,
		componentCode: string,
		action: 'accept_interview' | 'decline_interview' | 'accept_offer' | 'decline_offer',
	): Observable<{ header: OasisApplicationHeader }> {
		return this.http
			.post<OasisApiResponse<{ header: OasisApplicationHeader }>>(
				`${this.base}/oasis-application/${encodeURIComponent(id)}/components/${encodeURIComponent(componentCode)}/respond`,
				{ action },
			)
			.pipe(map((r) => this.unwrap(r)));
	}

	/**
	 * Branch on the envelope's `success` flag: return `payload` on success
	 * (including a null payload for a not-found read), or throw an
	 * {@link OasisApiError} on failure so it surfaces on the observable's error
	 * channel. Never branches on the HTTP status.
	 */
	private unwrap<T>(response: OasisApiResponse<T>): T {
		if (response.success) {
			return response.payload;
		}
		const err: OasisApiError = response.error ?? {
			code: response.status || 'ERROR',
			message: 'The request could not be completed.',
		};
		throw err;
	}
}
